import { useRef, useState } from "react";
import api from "../lib/api";
import { Upload, FileVideo, Image as ImageIcon, X, CheckCircle2, Loader2 } from "lucide-react";

/**
 * Reusable Cloudinary upload widget with progress bar.
 * Props:
 *  - kind: "video" | "image"
 *  - maxMB: number (default 100 for video, 10 for image)
 *  - onUploaded: (data) => void  // { secure_url, public_id, duration?, width?, height? }
 *  - currentUrl: optional preview URL for already-uploaded asset
 *  - testId: optional data-testid prefix
 */
export default function CloudinaryUpload({ kind = "video", maxMB, onUploaded, currentUrl, testId = "cloud-upload" }) {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(!!currentUrl);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);

  const limit = maxMB ?? (kind === "video" ? 100 : 10);
  const accept = kind === "video" ? "video/*" : "image/*";
  const Icon = kind === "video" ? FileVideo : ImageIcon;

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (kind === "video" && !f.type.startsWith("video/")) { setErr("Please select a video file"); return; }
    if (kind === "image" && !f.type.startsWith("image/")) { setErr("Please select an image file"); return; }
    if (f.size > limit * 1024 * 1024) { setErr(`File must be under ${limit}MB`); return; }
    setErr(""); setFile(f); setDone(false); setProgress(0);
    upload(f);
  };

  const upload = async (f) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const folder = kind === "video" ? "videos" : "images";
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api/upload/file?folder=" + folder;
        xhr.open("POST", apiBase);
        const tok = localStorage.getItem("hf_token");
        if (tok) xhr.setRequestHeader("Authorization", "Bearer " + tok);
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100)); };
        xhr.onload = () => {
          try {
            const j = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) resolve(j);
            else reject(new Error(j?.detail || "Upload failed"));
          } catch { reject(new Error("Upload failed")); }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send(fd);
      });

      setDone(true);
      // Pass back a Cloudinary-compatible shape so existing consumers keep working
      onUploaded?.({
        secure_url: data.secure_url || data.url,
        url: data.secure_url || data.url,
        public_id: data.path,
        duration: data.duration,
        format: data.format,
        bytes: data.size,
      });
    } catch (e) {
      setErr(e.message || "Upload failed");
      setDone(false);
    } finally { setUploading(false); }
  };

  const reset = () => { setFile(null); setDone(false); setProgress(0); setErr(""); if (ref.current) ref.current.value = ""; };

  if (currentUrl && done && !file) {
    return (
      <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2 text-xs">
        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0"/>
        <span className="flex-1 truncate text-white/80">Uploaded ✓</span>
        <button type="button" onClick={reset} className="text-white/60 hover:text-white text-xs">Replace</button>
      </div>
    );
  }

  if (!file) {
    return (
      <div data-testid={`${testId}-zone`}>
        <label className="flex flex-col items-center justify-center gap-2 w-full py-6 rounded-2xl border-2 border-dashed border-white/15 bg-white/5 cursor-pointer hover:border-[#00E5FF]/60 transition">
          <Icon className="w-7 h-7 text-white/50"/>
          <span className="text-xs text-white/70">Tap to upload {kind} (max {limit}MB)</span>
          <input ref={ref} type="file" accept={accept} onChange={pick} className="hidden" data-testid={`${testId}-input`}/>
        </label>
        {err && <div className="text-[#FF2A5F] text-xs mt-1" data-testid={`${testId}-err`}>{err}</div>}
      </div>
    );
  }

  return (
    <div className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid={`${testId}-progress`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#00E5FF]"/>
        <span className="text-xs flex-1 truncate">{file.name}</span>
        <span className="text-[10px] text-white/50">{(file.size/(1024*1024)).toFixed(1)}MB</span>
        <button type="button" onClick={reset} className="p-1 rounded-full hover:bg-white/10"><X className="w-3.5 h-3.5"/></button>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full bg-[#00E5FF] transition-all" style={{ width: `${done ? 100 : progress}%` }}/>
      </div>
      <div className="text-[10px] text-white/60 mt-1 flex items-center gap-1">
        {done ? <><CheckCircle2 className="w-3 h-3 text-emerald-400"/><span className="text-emerald-400">Uploaded</span></>
              : uploading ? <><Loader2 className="w-3 h-3 animate-spin"/><span>Uploading… {progress}%</span></>
              : <span>Ready</span>}
      </div>
      {err && <div className="text-[#FF2A5F] text-xs mt-1">{err}</div>}
    </div>
  );
}
